// ============================================================
// Instance DB backend — ONLY data source (no Firebase, no Supabase).
// DB: Postgres container animeforever-db on 130.210.56.43:5432
// App talks to it via same-origin /api/* (server/ + api/ + functions/
// proxy to PG). Table: public.videos (1499 rows, shared with AnimeForever).
//
// Same exports as the old Firebase module so callers are untouched:
// loadVideosOnce() resolves to an OBJECT keyed by row id (like RTDB /videos).
// ============================================================

// ── In-memory cache ──────────────────────────────────────────
let videosCache = null; // object: id -> normalized row
let videoIdToKeyMap = {}; // videoId -> id

const normalizeRow = (row) => {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    videoId: row.video_id ?? row.videoId,
    originalUrl: row.original_url ?? row.originalUrl,
    pluginUrl: row.plugin_url ?? row.pluginUrl,
    uploadDate: row.upload_date ?? row.uploadDate,
    durationFormatted: row.duration_formatted ?? row.durationFormatted,
    customTag: row.custom_tag ?? row.customTag,
    lengthSeconds: row.duration ?? row.lengthSeconds,
  };
};

const buildKeyMap = (videosObj) => {
  const map = {};
  if (!videosObj) return map;
  for (const k of Object.keys(videosObj)) {
    const item = videosObj[k];
    const possibleId = item?.videoId || item?.video_id || item?.video?.videoId || null;
    if (possibleId) {
      map[String(possibleId)] = item?.id || k;
      continue;
    }
    const url = item?.url || item?.video?.url || item?.videoUrl || item?.youtubeUrl;
    if (url) {
      try {
        const u = new URL(url);
        if (u.hostname.includes('youtu.be')) {
          const id = u.pathname.slice(1);
          if (id) map[String(id)] = item?.id || k;
        }
        if (u.searchParams && u.searchParams.has('v')) {
          map[String(u.searchParams.get('v'))] = item?.id || k;
        }
      } catch (e) {
        // ignore malformed URLs when deriving ids
      }
    }
  }
  return map;
};

const apiFetch = async (path, options) => {
  const res = await fetch(path, options);
  if (!res.ok) throw new Error(`instance api ${res.status} for ${path}`);
  return res.json();
};

// Load videos once — returns OBJECT keyed by id (RTDB-shaped contract).
export const loadVideosOnce = async () => {
  const PAGE = 1000;
  const val = {};
  for (let offset = 0; ; offset += PAGE) {
    const data = await apiFetch(`/api/videos?limit=${PAGE}&offset=${offset}`);
    const batch = Array.isArray(data) ? data : data.videos || [];
    if (batch.length === 0) break;
    for (const row of batch.map(normalizeRow)) {
      if (row && row.id) val[row.id] = row;
    }
    if (batch.length < PAGE) break;
  }
  const out = Object.keys(val).length ? val : null;
  videosCache = out;
  videoIdToKeyMap = buildKeyMap(val);
  return out;
};

// Subscribe (load once; no realtime channel on the instance DB).
export const subscribeToVideos = (callback) => {
  loadVideosOnce().then((val) => {
    if (val) callback(val);
  });
  return () => {};
};

// Add a video entry via the instance API — returns created id.
export const addVideo = async (videoObj) => {
  const row = {
    video_id: videoObj.videoId ?? videoObj.video_id,
    title: videoObj.title,
    url: videoObj.url,
    original_url: videoObj.originalUrl ?? videoObj.original_url,
    platform: videoObj.platform,
    source: videoObj.source,
    author: videoObj.author ?? 'Uploaded',
    thumbnails: videoObj.thumbnails ?? null,
    thumbnail: videoObj.thumbnail ?? null,
    duration: videoObj.lengthSeconds ?? videoObj.duration ?? 0,
    tags: videoObj.tags ?? null,
    episode: videoObj.episode ?? null,
    upload_date: videoObj.uploadDate ?? videoObj.upload_date ?? null,
    language: videoObj.language ?? 'tamil',
    series: videoObj.series ?? 'Naruto',
    views: 0,
    likes: 0,
  };
  const created = await apiFetch('/api/videos', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(row),
  });
  const rec = created && (created.video || created);
  const n = rec && rec.id ? normalizeRow(rec) : null;
  if (n && videosCache) {
    videosCache[n.id] = n;
    videoIdToKeyMap = buildKeyMap(videosCache);
  }
  return n ? n.id : null;
};

// find the row id for a public videoId
const findVideoKeyByVideoId = async (videoId) => {
  if (videoIdToKeyMap && videoIdToKeyMap[String(videoId)]) {
    return videoIdToKeyMap[String(videoId)];
  }
  if (!videosCache) {
    await loadVideosOnce().catch(() => null);
    if (videoIdToKeyMap[String(videoId)]) return videoIdToKeyMap[String(videoId)];
  } else {
    const keys = Object.keys(videosCache);
    for (const k of keys) {
      const item = videosCache[k];
      if (item?.videoId === videoId || item?.video?.videoId === videoId) return item?.id || k;
    }
  }
  try {
    const data = await apiFetch(`/api/videos?video_id=${encodeURIComponent(String(videoId))}`);
    const rows = Array.isArray(data) ? data : data.videos || [];
    if (rows && rows[0]) {
      const n = normalizeRow(rows[0]);
      videoIdToKeyMap[String(videoId)] = n.id;
      return n.id;
    }
  } catch (e) {
    // lookup miss — caller treats null as not found
  }
  return null;
};

// increment views via the instance-backed endpoint (server is atomic)
export const incrementView = async (videoId) => {
  const key = await findVideoKeyByVideoId(videoId);
  if (!key) return null;
  await sendIncrement({ id: key, type: 'views', delta: 1 });
  return key;
};

// change likes by delta via the instance-backed endpoint
export const changeLikes = async (videoId, delta) => {
  const key = await findVideoKeyByVideoId(videoId);
  if (!key) return null;
  await sendIncrement({ id: key, type: 'likes', delta });
  return key;
};

const sendIncrement = async (payload) => {
  try {
    const res = await fetch('/api/increment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    if (!res.ok) console.warn('increment API failed, updating cache only');
  } catch (e) {
    console.warn('increment API error, updating cache only', e);
  }
  if (videosCache && videosCache[payload.id] && payload.type in videosCache[payload.id]) {
    const row = videosCache[payload.id];
    row[payload.type] = Math.max(0, (Number(row[payload.type]) || 0) + (Number(payload.delta) || 0));
  }
};

const firebaseApi = { loadVideosOnce, subscribeToVideos, addVideo, incrementView, changeLikes };

export default firebaseApi;
